import Vue from 'vue';
import Vuex from 'vuex';
import Cookies from 'js-cookie';
import { i18n, waitForTranslation } from '@/plugins/I18n.js';
import { operatingSystems } from '@/data';
import { isIDevice } from '@/utils';
import universalDetails from './modules/universalDetails';
import standaloneApp from './modules/standaloneApp';
import router from './modules/router';

Vue.use(Vuex);

function safelyParseJSON(json) {
  try {
    return JSON.parse(json);
  } catch {
    return json;
  }
}

function getDeviceType(device) {
  if (/iphone/i.test(device.model)) {
    return 'iPhone';
  }

  if (/ipad/i.test(device.model)) {
    return 'iPad';
  }

  if (/ipod/i.test(device.model)) {
    return 'iPod';
  }

  if (/appletv/i.test(device.model)) {
    return 'Apple TV';
  }

  if (/mac/i.test(device.model)) {
    return 'Mac';
  }

  return 'Unknown';
}

function createStore() {
  return new Vuex.Store({
    modules: {
      universalDetails,
      standaloneApp,
      router,
    },
    state() {
      return {
        dataFetchPromise: null,
        isDataFetched: false,
        areAllCookiesAllowed: safelyParseJSON(localStorage.getItem('areAllCookiesAllowed')),
        theme: localStorage.getItem('theme') || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'),
        packageVersion: process.env.PACKAGE_VERSION || '0',
        lang: localStorage.getItem('lang') || 'en',
        dictionary: {},
        lt: localStorage.getItem('lt'),
        st: localStorage.getItem('st'),
        notifyAboutPublisherAuthorization: false,
        ref: localStorage.getItem('ref'),
        devices: [],
        devicesLoaded: false,
        currentDevice: null,
        isSearchExpanded: false,
        genres: {
          ios: {}, cydia: {}, tvos: {}, official: {},
        },
        config: {},
        publisherProfile: {},
        customerProfile: {},
        configLoaded: false,
        promos: {
          pages: [],
          content: [],
          banners: [],
        },
        stats: {},
      };
    },
    mutations: {
      setAreAllCookiesAllowed(state, payload) {
        state.areAllCookiesAllowed = payload;
        localStorage.setItem('areAllCookiesAllowed', JSON.stringify(payload));
      },
      setTheme(state, payload) {
        const oldTheme = payload === 'light' ? 'dark' : 'light';
        document.documentElement.classList.remove(oldTheme);
        document.documentElement.classList.add(payload);
        localStorage.setItem('theme', payload);
        state.theme = payload;
      },
      setDevices(state, payload) {
        state.devices = payload;

        // Set the current device to the first device in the list if available
        if (payload && payload.length > 0) {
          // If there are important_messages in the config, include them in the current device
          if (state.config && state.config.important_messages) {
            state.currentDevice = {
              ...payload[0],
              important_messages: state.config.important_messages
            };
          } else {
            state.currentDevice = payload[0];
          }
        } else {
          state.currentDevice = null;
        }

        if (!state.devicesLoaded) {
          state.devicesLoaded = true;
          setTimeout(() => {
            const toProfile = Vue.router.currentRoute.matched[0].path === '/my';
            const { authRequired } = Vue.router.currentRoute.meta;
            if (!state.devices.length && toProfile && authRequired) {
              Vue.router.push('/my/error');
            }
          }, 200);
        }
      },
      setConfig(state, payload) {
        state.configLoaded = true;
        state.config = payload;

        // If we have a current device, update it to include important_messages if present
        if (state.devices && state.devices.length && payload && payload.important_messages) {
          // Find the current device (which would be the first one in the list)
          const currentDevice = state.devices[0];

          // Set the important_messages for the current device
          state.currentDevice = {
            ...currentDevice,
            important_messages: payload.important_messages
          };
        }
      },
      setCustomerProfile(state, payload) {
        state.customerProfile = payload;
      },
      setPublisherProfile(state, payload) {
        state.publisherProfile = payload;
      },
      isSearchExpanded(state, payload) {
        state.isSearchExpanded = payload;
      },
      setGenres(state, payload) {
        state.genres = payload;
      },
      lt(state, payload) {
        if (!payload) {
          localStorage.removeItem('lt');
          state.lt = null;
          return;
        }

        localStorage.setItem('lt', payload);
        state.lt = payload;
      },
      st(state, payload) {
        if (!payload) {
          localStorage.removeItem('st');
          state.st = null;
          return;
        }

        localStorage.setItem('st', payload);
        state.st = payload;
      },
      notifyAboutPublisherAuthorization(state, payload) {
        state.notifyAboutPublisherAuthorization = payload;
      },
      ref(state, payload) {
        if (state.ref) {
          return;
        }
        localStorage.setItem('ref', payload);
        state.ref = payload;
      },
      lang(state, payload) {
        state.lang = payload;
      },
      promos(state, payload) {
        state.promos = payload;
      },
      stats(state, payload) {
        state.stats = payload;
      },
    },
    actions: {
      parseTokens(context) {
        if (Cookies.get('lt')) {
          context.commit('lt', Cookies.get('lt'));
          Cookies.remove('lt', {
            path: '',
            domain: '.appdb.to',
          });
        }

        try {
          const params = new URLSearchParams(window.location.search);
          const newParams = new URLSearchParams(window.location.search);

          if (params.get('lt')) {
            context.commit('lt', params.get('lt'));
            newParams.delete('lt');
          }
          if (params.get('st')) {
            context.commit('st', params.get('st'));
            context.commit('notifyAboutPublisherAuthorization', true);
            newParams.delete('st');
          }
          if (params.get('ref')) {
            context.commit('ref', params.get('ref'));
            newParams.delete('ref');
          }

          if (params.size === newParams.size) {
            return;
          }

          Vue.router.onReady(() => {
            const query = Object.fromEntries(newParams.entries());
            Vue.router.replace({ query });
          });
        } catch {
          /**/
        }
      },
      async fetchData(context) {
        if (context.state.dataFetchPromise) {
          return context.state.dataFetchPromise;
        }

        context.dispatch('parseTokens');

        context.state.dataFetchPromise = (async () => {
          await Promise.allSettled([
            context.dispatch('getPublisherProfile'),
            context.dispatch('getCustomerProfile'),
          ]);

          await Promise.allSettled([
            context.dispatch('getConfig', false),
            context.dispatch('getGenres'),
            context.dispatch('getPromos'),
            context.dispatch('getDevices', false),
          ]);

          context.state.isDataFetched = true;
        })();

        return context.state.dataFetchPromise;
      },
      clearLinkedDeviceData(context) {
        context.commit('lt', '');
        context.dispatch('getDevices');
        context.dispatch('getConfig');
        context.dispatch('getCustomerProfile');
      },
      getGenres(context) {
        return Vue.prototype.$api.getGenres().then((data) => {
          const genres = {};
          /* eslint-disable-next-line */
          for (const category of Object.keys(data)) {
            genres[category] = Object.fromEntries(data[category].map(genre => [genre.id, genre]));
          }
          context.commit('setGenres', genres);
        });
      },
      getDevices(context, handleErrors) {
        if (!context.getters.lt) {
          context.commit('setDevices', []);
          return;
        }
        // eslint-disable-next-line
        return Vue.prototype.$api.getDevices(handleErrors).then((response) => {
          if (response.success) {
            const devices = (response.data || []).map((device) => {
              const type = getDeviceType(device);

              const newDevice = {
                lt: device.link_token,
                model: device.nice_idevice_model,
                name: device.name,
                ios: device.ios_version,
                plus: device.is_plus === 'yes' || Date.now() < +new Date(device.plus_till * 1000),
                premium: device.is_premium === 'yes' || Date.now() < +new Date(device.premium_till * 1000),
                email: device.email,
                can_use_jit: device.can_use_jit,
                can_use_jit_validation_error: device.can_use_jit_validation_error,
                can_use_free_developer_account: device.can_use_free_developer_account,
                can_use_free_developer_account_validation_error: device.can_use_free_developer_account_validation_error,
                link_type: device.link_type,
                type,
              };

              newDevice.icon = require('@/assets/iphone.png');
              if (/pad/i.test(newDevice.model)) {
                newDevice.icon = require('@/assets/ipad.png');
              } else if (/tv/i.test(newDevice.model)) {
                newDevice.icon = require('@/assets/appletv.png');
              } else if (/mac/i.test(newDevice.model)) {
                newDevice.icon = require('@/assets/mac.png');
              }

              newDevice.title = `${newDevice.ios}`;

              return newDevice;
            });

            context.commit('setDevices', devices);
          } else {
            context.commit('setDevices', []);
          }
        });
      },
      getConfig(context, handleErrors) {
        return new Promise((resolve, reject) => {
          if (!context.getters.lt) {
            context.commit('setConfig', {});
            resolve();
            return;
          }
          Vue.prototype.$api.getConfiguration(handleErrors).then((config) => {
            const result = Object.keys(config).includes('success') ? config.data : config;
            context.commit('setConfig', result);
            resolve(result);
          }).catch(reject);
        });
      },
      async getPublisherProfile(context) {
        if (!context.getters.st) {
          context.commit('setPublisherProfile', {});
          return;
        }

        const response = await Vue.prototype.$api.getPublisherProfile();
        if (!response.success) {
          context.commit('setPublisherProfile', {});
          return;
        }

        context.commit('setPublisherProfile', response.data);
        if (context.getters.notifyAboutPublisherAuthorization) {
          await waitForTranslation();
          Vue.notify({
            type: 'success',
            text: i18n.t('PUBLISHER_AUTHORIZED_NOTIFICATION'),
          });
        }
      },
      async getCustomerProfile(context) {
        if (!context.getters.lt) {
          context.commit('setCustomerProfile', {});
          return;
        }

        const response = await Vue.prototype.$api.getCustomerProfile();
        if (!response.success) {
          context.commit('setCustomerProfile', {});
          return;
        }

        context.commit('setCustomerProfile', response.data);
      },
      setLang(context, payload) {
        localStorage.setItem('lang', payload);
        context.commit('lang', payload);
        i18n.changeLanguage(payload);
      },
      getStats(context) {
        if (context.stats) {
          return Promise.resolve(context.stats);
        }

        return Vue.prototype.$api.getStats().then((response) => {
          context.commit('stats', response);

          return response;
        });
      },
      async getPromos(context) {
        const promos = await Vue.prototype.$api.getPromotions();
        context.commit('promos', promos);
        return promos;
      },
    },
    getters: {
      appVersion: state => state.packageVersion,
      isDeviceLinked(state) {
        return !!Object.keys(state.config).length || state.devices.length;
      },
      devices(state) {
        return state.devices;
      },
      currentDevice(state) {
        return state.devices.find(device => device.lt === state.lt) || null;
      },
      currentDeviceOS(state) {
        if (!state.config.model) {
          return null;
        }

        return operatingSystems.find(os => state.config.model.toLowerCase().includes(os.device.toLowerCase()))?.name || null;
      },
      devicesLoaded(state) {
        return state.devicesLoaded;
      },
      config(state) {
        return state.config;
      },
      configLoaded(state) {
        return state.configLoaded;
      },
      isSearchExpanded(state) {
        return state.isSearchExpanded;
      },
      genres(state) {
        return state.genres;
      },
      lt(state) {
        return state.lt;
      },
      st(state) {
        return state.st;
      },
      notifyAboutPublisherAuthorization(state) {
        return state.notifyAboutPublisherAuthorization;
      },
      ref(state) {
        return state.ref;
      },
      lang(state) {
        return state.lang;
      },
      promos(state) {
        return state.promos;
      },
      hasActivePlus(state) {
        // eslint-disable-next-line camelcase
        return state.config?.is_plus === 'yes';
      },
      hasActivePremium(state) {
        // eslint-disable-next-line camelcase
        return state.config?.is_premium === 'yes';
      },
      hasCanceledPlus(state) {
        // eslint-disable-next-line camelcase
        return Date.now() < +new Date(state.config?.plus_till * 1000);
      },
      hasCanceledPremium(state) {
        // eslint-disable-next-line camelcase
        return Date.now() < +new Date(state.config?.premium_till * 1000);
      },
      hasPlus(state, getters) {
        return getters.hasActivePlus || getters.hasCanceledPlus;
      },
      hasPremium(state, getters) {
        return getters.hasActivePremium || getters.hasCanceledPremium;
      },
      email(state) {
        return state.config?.email || '';
      },
      canInstallApps(state) {
        if (state.config.link_type === 'mdm') {
          return true;
        }

        if (state.config.link_type === 'profile') {
          return isIDevice() || state.config.model.toLowerCase().includes('mac');
        }

        return false;
      },
      importantMessages(state) {
        // Check for messages in currentDevice first, then in config if not found
        if (state.currentDevice && state.currentDevice.important_messages) {
          return state.currentDevice.important_messages;
        } else if (state.config && state.config.important_messages) {
          return state.config.important_messages;
        }
        return [];
      },
    },
  });
}

export default createStore;
